import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import DobPicker from '../../components/registration/DobPicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { useAuth } from '../../context/AuthContext';
import { Users, Plus, Edit, Loader2, Coins, Shirt, School, Info, Check, ChevronsUpDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const tshirtSizes = [
  { value: 'YXS', label: 'Youth XS' },
  { value: 'YS', label: 'Youth S' },
  { value: 'YM', label: 'Youth M' },
  { value: 'YL', label: 'Youth L' },
  { value: 'YXL', label: 'Youth XL' },
  { value: 'AS', label: 'Adult S' },
  { value: 'AM', label: 'Adult M' },
  { value: 'AL', label: 'Adult L' },
  { value: 'AXL', label: 'Adult XL' },
  { value: 'AXXL', label: 'Adult XXL' },
];

export default function LearnersPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLearner, setEditingLearner] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Partner schools state
  const [partnerSchools, setPartnerSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolPopoverOpen, setSchoolPopoverOpen] = useState(false);
  const [useCustomSchool, setUseCustomSchool] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    grade: '',
    tshirt_size: '',
    partner_school: '', // UUID of partner school
    school: '', // Custom school name (text)
    allergies: '',
    medical_notes: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    accessibility_notes: '',
    photo_consent: false,
  });

  useEffect(() => {
    fetchLearners();
    fetchPartnerSchools();
  }, []);

  const fetchLearners = async () => {
    try {
      const response = await api.get('/learners/');
      // Handle both paginated (Django) and non-paginated responses
      const data = response.data?.results || response.data || [];
      setLearners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching learners:', error);
      toast.error('Failed to load learners');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerSchools = async () => {
    setSchoolsLoading(true);
    try {
      const response = await api.get('/partner/schools/');
      const data = response.data?.results || response.data || [];
      // Only show active schools
      const activeSchools = Array.isArray(data) ? data.filter(s => s.active !== false) : [];
      setPartnerSchools(activeSchools);
    } catch (error) {
      console.error('Error fetching partner schools:', error);
      // Don't show error toast - schools list is optional
    } finally {
      setSchoolsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      grade: '',
      tshirt_size: '',
      partner_school: '',
      school: '',
      allergies: '',
      medical_notes: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      accessibility_notes: '',
      photo_consent: false,
    });
    setEditingLearner(null);
    setUseCustomSchool(false);
  };

  const handleOpenDialog = (learner = null) => {
    if (learner) {
      setEditingLearner(learner);
      const hasPartnerSchool = !!learner.partner_school;
      setUseCustomSchool(!hasPartnerSchool && !!learner.school);
      setFormData({
        first_name: learner.first_name || '',
        last_name: learner.last_name || '',
        date_of_birth: learner.date_of_birth || '',
        grade: learner.grade || '',
        tshirt_size: learner.tshirt_size || '',
        partner_school: learner.partner_school || '',
        school: learner.school || '',
        allergies: learner.allergies || '',
        medical_notes: learner.medical_notes || '',
        emergency_contact_name: learner.emergency_contact_name || '',
        emergency_contact_phone: learner.emergency_contact_phone || '',
        accessibility_notes: learner.accessibility_notes || '',
        photo_consent: learner.photo_consent || false,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.date_of_birth || 
        !formData.grade || !formData.emergency_contact_name || !formData.emergency_contact_phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      // Prepare data - only send partner_school OR school, not both
      const submitData = {
        ...formData,
        partner_school: useCustomSchool ? null : (formData.partner_school || null),
        school: useCustomSchool ? formData.school : '',
      };

      if (editingLearner) {
        await api.put(`/learners/${editingLearner.id}/`, submitData);
        toast.success('Learner updated successfully');
      } else {
        await api.post('/learners/', submitData);
        toast.success('Learner added successfully');
        // Check for pending camp registration — redirect back to summer camps
        const campContext = localStorage.getItem('pendingCampRegistration');
        if (campContext) {
          try {
            const ctx = JSON.parse(campContext);
            toast.success('Now let\'s finish your camp registration!', { duration: 3000 });
            setTimeout(() => navigate(`/summer-camps?register=${ctx.campId || ''}`), 1000);
            return;
          } catch {}
        }
      }
      await fetchLearners();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save learner';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const getSelectedSchool = () => {
    if (!formData.partner_school) return null;
    return partnerSchools.find(s => s.id === formData.partner_school);
  };

  const getSchoolDisplayName = (learner) => {
    if (learner.partner_school_name) {
      return learner.partner_school_name;
    }
    if (learner.school) {
      return learner.school;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-rsc-navy" data-testid="learners-title">
            My Learners
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your children's profiles and enrollment information
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-learner-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Learner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingLearner ? 'Edit Learner' : 'Add New Learner'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    data-testid="learner-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    data-testid="learner-lastname-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth *</Label>
                  <DobPicker
                    value={formData.date_of_birth}
                    onChange={(iso) => setFormData({ ...formData, date_of_birth: iso })}
                    placeholder="Select date of birth"
                    testId="learner-dob-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="grade">Grade Level *</Label>
                  <Select value={formData.grade} onValueChange={(v) => setFormData({ ...formData, grade: v })}>
                    <SelectTrigger data-testid="learner-grade-select">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {grades.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade === 'K' ? 'Kindergarten' : `Grade ${grade}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* School Selection Section */}
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2">
                  <School className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold text-blue-900">School Information</Label>
                </div>
                
                <Alert className="bg-white border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm text-blue-800">
                    If your child attends a Ready Set STEM partner school, they may have access to exclusive courses. 
                    Select their school below to unlock these opportunities.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  {/* Partner School Selector */}
                  {!useCustomSchool && (
                    <div className="space-y-2">
                      <Label>Partner School</Label>
                      <Popover open={schoolPopoverOpen} onOpenChange={setSchoolPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={schoolPopoverOpen}
                            className="w-full justify-between font-normal"
                            data-testid="school-selector"
                          >
                            {formData.partner_school ? (
                              <span className="flex items-center gap-2">
                                <School className="h-4 w-4 text-blue-600" />
                                {getSelectedSchool()?.name || 'Select school...'}
                                {getSelectedSchool()?.district_name && (
                                  <Badge variant="secondary" className="ml-2 text-xs">
                                    {getSelectedSchool()?.district_name}
                                  </Badge>
                                )}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Search for your school...</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search schools..." />
                            <CommandList>
                              <CommandEmpty>
                                {schoolsLoading ? (
                                  <div className="flex items-center justify-center py-4">
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Loading schools...
                                  </div>
                                ) : (
                                  <div className="py-4 text-center">
                                    <p className="text-sm text-muted-foreground">No partner schools found.</p>
                                    <Button
                                      variant="link"
                                      className="mt-2"
                                      onClick={() => {
                                        setUseCustomSchool(true);
                                        setSchoolPopoverOpen(false);
                                      }}
                                    >
                                      Enter school manually instead
                                    </Button>
                                  </div>
                                )}
                              </CommandEmpty>
                              <CommandGroup heading="Partner Schools">
                                {partnerSchools.map((school) => (
                                  <CommandItem
                                    key={school.id}
                                    value={`${school.name} ${school.district_name || ''}`}
                                    onSelect={() => {
                                      setFormData({ ...formData, partner_school: school.id, school: '' });
                                      setSchoolPopoverOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        formData.partner_school === school.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span>{school.name}</span>
                                      {school.district_name && (
                                        <span className="text-xs text-muted-foreground">
                                          {school.district_name}
                                        </span>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      
                      {formData.partner_school && (
                        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                          <Check className="h-4 w-4" />
                          <span>Your child may be eligible for exclusive courses at this school!</span>
                        </div>
                      )}
                      
                      <button
                        type="button"
                        onClick={() => setUseCustomSchool(true)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        My school is not listed
                      </button>
                    </div>
                  )}

                  {/* Custom School Input */}
                  {useCustomSchool && (
                    <div className="space-y-2">
                      <Label htmlFor="school">School Name</Label>
                      <Input
                        id="school"
                        value={formData.school}
                        onChange={(e) => setFormData({ ...formData, school: e.target.value, partner_school: '' })}
                        placeholder="Enter your child's school name"
                        data-testid="learner-school-input"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setUseCustomSchool(false);
                          setFormData({ ...formData, school: '' });
                        }}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        ← Search partner schools instead
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tshirt_size" className="flex items-center gap-1">
                    <Shirt className="h-4 w-4" />
                    T-Shirt Size
                  </Label>
                  <Select value={formData.tshirt_size} onValueChange={(v) => setFormData({ ...formData, tshirt_size: v })}>
                    <SelectTrigger data-testid="learner-tshirt-select">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {tshirtSizes.map((size) => (
                        <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">For course completion rewards</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="allergies">Allergies</Label>
                <Textarea
                  id="allergies"
                  placeholder="List any food or environmental allergies"
                  value={formData.allergies}
                  onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  data-testid="learner-allergies-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="medical_notes">Medical Notes</Label>
                <Textarea
                  id="medical_notes"
                  placeholder="Any medical conditions or medications"
                  value={formData.medical_notes}
                  onChange={(e) => setFormData({ ...formData, medical_notes: e.target.value })}
                  data-testid="learner-medical-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_name">Emergency Contact Name *</Label>
                  <Input
                    id="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    data-testid="learner-emergency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_phone">Emergency Contact Phone *</Label>
                  <Input
                    id="emergency_contact_phone"
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    data-testid="learner-emergency-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accessibility_notes">Accessibility Notes</Label>
                <Textarea
                  id="accessibility_notes"
                  placeholder="Any accessibility requirements or accommodations needed"
                  value={formData.accessibility_notes}
                  onChange={(e) => setFormData({ ...formData, accessibility_notes: e.target.value })}
                  data-testid="learner-accessibility-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="photo_consent"
                  checked={formData.photo_consent}
                  onCheckedChange={(checked) => setFormData({ ...formData, photo_consent: checked })}
                  data-testid="learner-photo-consent"
                />
                <label htmlFor="photo_consent" className="text-sm leading-tight">
                  I consent to photographs/videos of my child being used for promotional purposes
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-learner-btn">
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {editingLearner ? 'Save Changes' : 'Add Learner'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {learners.length === 0 ? (
        <Card className="card-marketing">
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <Users className="h-12 w-12 text-muted-foreground" />
            <div className="text-center">
              <h3 className="font-semibold text-lg">No learners yet</h3>
              <p className="text-muted-foreground">Add your first learner to start enrolling in courses</p>
            </div>
            <Button onClick={() => handleOpenDialog()} data-testid="add-first-learner-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Learner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {learners.map((learner, index) => (
            <Card 
              key={learner.id} 
              className="card-marketing cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all group" 
              data-testid={`learner-card-${index}`}
              onClick={() => navigate(`/parent/progress?learner_id=${learner.id}`)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">
                      {learner.first_name} {learner.last_name}
                    </h3>
                    <p className="text-muted-foreground">
                      {learner.grade === 'K' ? 'Kindergarten' : `Grade ${learner.grade}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(learner);
                      }}
                      data-testid={`edit-learner-${index}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>

                <div className="space-y-2 text-sm mt-4">
                  {getSchoolDisplayName(learner) && (
                    <div className="flex items-center gap-2">
                      <School className="h-4 w-4 text-blue-600" />
                      <span className="text-muted-foreground">{getSchoolDisplayName(learner)}</span>
                      {learner.partner_school && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                          Partner
                        </Badge>
                      )}
                    </div>
                  )}
                  {learner.district_name && (
                    <p className="text-xs text-muted-foreground ml-6">
                      District: {learner.district_name}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-secondary font-medium">
                    <Coins className="h-4 w-4" />
                    {learner.points_balance || 0} points
                  </div>
                </div>
                
                {/* View Progress hint */}
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground group-hover:text-primary transition-colors flex items-center gap-1">
                    Click to view progress
                    <ChevronRight className="h-3 w-3" />
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
